import apiClient from '@/lib/axios';
import type {
  JobPayment,
  PaymentSummary,
  CreateJobPaymentRequest,
  UpdateJobPaymentRequest,
} from '../types/jobPayment';

/**
 * Get all payments for a job
 */
export const getJobPayments = async (
  jobId: number,
  isForecast?: boolean
): Promise<JobPayment[]> => {
  const params = new URLSearchParams();
  if (isForecast !== undefined) {
    params.append('isForecast', isForecast.toString());
  }

  const response = await apiClient.get<{ data: JobPayment[] }>(
    `/jobs/${jobId}/payments${params.toString() ? `?${params.toString()}` : ''}`
  );
  return response.data.data;
};

/**
 * Get payment summary for a job
 */
export const getJobPaymentSummary = async (
  jobId: number,
  isForecast?: boolean
): Promise<PaymentSummary> => {
  const params = new URLSearchParams();
  if (isForecast !== undefined) {
    params.append('isForecast', isForecast.toString());
  }

  const response = await apiClient.get<{ data: PaymentSummary }>(
    `/jobs/${jobId}/payments/summary${params.toString() ? `?${params.toString()}` : ''}`
  );
  return response.data.data;
};

/**
 * Get a specific payment by ID
 */
export const getJobPaymentById = async (
  jobId: number,
  paymentId: number
): Promise<JobPayment> => {
  const response = await apiClient.get<{ data: JobPayment }>(
    `/jobs/${jobId}/payments/${paymentId}`
  );
  return response.data.data;
};

/**
 * Create a new job payment
 */
export const createJobPayment = async (
  jobId: number,
  data: CreateJobPaymentRequest
): Promise<JobPayment> => {
  const response = await apiClient.post<{ data: JobPayment }>(
    `/jobs/${jobId}/payments`,
    data
  );
  return response.data.data;
};

/**
 * Update an existing job payment
 */
export const updateJobPayment = async (
  jobId: number,
  paymentId: number,
  data: UpdateJobPaymentRequest
): Promise<JobPayment> => {
  const response = await apiClient.put<{ data: JobPayment }>(
    `/jobs/${jobId}/payments/${paymentId}`,
    data
  );
  return response.data.data;
};

/**
 * Mark a pending payment as cleared
 */
export const clearJobPayment = async (
  jobId: number,
  paymentId: number
): Promise<JobPayment> => {
  const response = await apiClient.put<{ data: JobPayment }>(
    `/jobs/${jobId}/payments/${paymentId}/clear`
  );
  return response.data.data;
};

/**
 * Delete a job payment
 */
export const deleteJobPayment = async (
  jobId: number,
  paymentId: number
): Promise<void> => {
  await apiClient.delete(`/jobs/${jobId}/payments/${paymentId}`);
};
